import React, { useCallback, useMemo } from 'react';
import { Avatar, List } from 'antd';
import { formatDate } from 'utils/textUtils';
import { ENQUIRY_TYPES } from 'configs/localData';
import i18next from 'i18next';
import { getSunMoonTime } from 'utils/formatFieldUtils';
import { MessagesItem } from 'types/enquiries';
import { AvatarPlaceholderIcon } from 'components/common/SVGIcon';
import { InboxItemStyles } from './styles';

interface Props {
  record?: Partial<MessagesItem>;
  handleClickInbox?: (value: string) => void;
  checkIsActiveInbox?: (value: string) => boolean;
}

const InboxItem = ({
  record = {},
  handleClickInbox,
  checkIsActiveInbox,
}: Props) => {
  const isBlur = record?.isBlur;
  const currentType = ENQUIRY_TYPES.find(
    (enquiryType) => enquiryType.value === record?.enquiryType?.name,
  );

  const isActiveInbox = useMemo(
    () => checkIsActiveInbox(record.id),
    [checkIsActiveInbox, record.id],
  );

  const getMessageContent = useCallback(
    (firstMessage) => {
      if (firstMessage?.message) return firstMessage?.message;

      if (firstMessage?.files?.length > 0) {
        const fileContent =
          record.centreId === firstMessage?.senderId
            ? `${i18next.t('messages.you')}`
            : `${firstMessage?.senderFirstName || ''} ${
                firstMessage?.senderLastName || ''
              }`;

        return `${fileContent} ${i18next.t('messages.contentSendFile')}`;
      }

      return firstMessage?.message || i18next.t('error.waiting');
    },
    [record.centreId],
  );

  const onClickInbox = () => {
    handleClickInbox(record.id);
  };

  return (
    <InboxItemStyles isBlur={isBlur}>
      <List.Item
        key={record?.id}
        onClick={onClickInbox}
        className={`${record?.centreHasNewMessage && 'not-seen'} ${
          isActiveInbox && 'current-message'
        }`}
      >
        <List.Item.Meta
          avatar={
            <div>
              <Avatar
                size={40}
                src={record?.user?.avatar}
                icon={<AvatarPlaceholderIcon className="text-gray-400" />}
              />
            </div>
          }
          title={
            <div className="title-section">
              <div className="name-section">
                <div className="title">
                  {`${record?.user?.firstName ? record?.user?.firstName : ''} `}
                  <span className={isBlur ? 'blur' : ''}>{`${
                    record?.user?.lastName ? record?.user?.lastName : ''
                  }`}</span>
                </div>
                <span className="date text-12">
                  {formatDate(record?.latestMessage?.createdAt, 'D MMM YYYY')}
                  {getSunMoonTime(record?.latestMessage?.createdAt)}
                </span>
              </div>
              <div className="type-section">
                <div
                  className="dot-type"
                  style={{ background: currentType?.dotColor }}
                />
                <span className="name-type">
                  {i18next.t(currentType?.text)}
                </span>
              </div>
            </div>
          }
          description={
            <span className="text-gray-500">
              {getMessageContent(record?.latestMessage)}
            </span>
          }
        />
      </List.Item>
    </InboxItemStyles>
  );
};

export default InboxItem;
